import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, ArrowRight, Calendar, Clock } from 'lucide-react';

const TRIGGER_DELAY_MS = 15_000; // Show after 15s on the page

const WEEKDAYS = ['nedeljo', 'ponedeljek', 'torek', 'sredo', 'četrtek', 'petek', 'soboto'];
const MONTHS = ['januarja', 'februarja', 'marca', 'aprila', 'maja', 'junija', 'julija', 'avgusta', 'septembra', 'oktobra', 'novembra', 'decembra'];

function formatPersonalDate(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (d.getTime() === today.getTime()) return 'še danes';
  if (d.getTime() === tomorrow.getTime()) return 'že jutri';
  return `v ${WEEKDAYS[d.getDay()]} ${d.getDate()}. ${MONTHS[d.getMonth()]}`;
}

/**
 * Friendly "Fizio team here" popup — triggered after 15s on the page, once per session.
 * Skipped on /admin, /coming-soon, and when booking modal is being opened.
 */
export default function ScrollBookingNudge() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);
  const [slot, setSlot] = useState(null);
  const triggeredRef = useRef(false);

  // Decide whether to show on this route
  const isExcludedRoute = pathname.startsWith('/admin')
    || pathname === '/coming-soon'
    || new URLSearchParams(search).get('book') === 'true';

  useEffect(() => {
    if (isExcludedRoute) return;
    if (typeof window === 'undefined') return;

    // Trigger after TRIGGER_DELAY_MS on the page — fetches FRESH slot at that moment
    // Re-shows on every page load / refresh (no session cache)
    const timer = setTimeout(() => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      axios.get('/api/next-available-slots?service_id=1&limit=1')
        .then(res => setSlot((res.data?.slots || [])[0] || null))
        .catch(() => setSlot(null))
        .finally(() => setVisible(true));
    }, TRIGGER_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isExcludedRoute]);

  // Radix Dialog (BookingModal) occasionally leaves `pointer-events: none` on
  // <body> after it closes (known Radix issue). If nudge appears right after
  // the BookingModal was closed, its X button becomes un-clickable. When the
  // nudge becomes visible we defensively clear any leaked style so the popup
  // is fully interactive.
  useEffect(() => {
    if (!visible) return;
    const clearLeakedPointerEvents = () => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    };
    clearLeakedPointerEvents();
    // Run again on next tick to catch late-fired Radix cleanup timers.
    const t1 = setTimeout(clearLeakedPointerEvents, 100);
    const t2 = setTimeout(clearLeakedPointerEvents, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [visible]);

  const dismiss = (e) => {
    if (e && e.preventDefault) { e.preventDefault(); e.stopPropagation(); }
    setClosed(true);
    setTimeout(() => setVisible(false), 250);
  };

  const goToBooking = () => {
    if (slot) {
      navigate(`/?book=true&date=${slot.date}&time=${slot.time}${slot.therapist_id ? `&therapist=${slot.therapist_id}` : ''}`);
    } else {
      navigate('/?book=true');
    }
    dismiss();
  };

  const goToBookingPickOwn = () => {
    // Open booking flow without pre-fill so user can pick a different slot
    navigate('/?book=true');
    dismiss();
  };

  if (!visible || isExcludedRoute) return null;

  const dateLabel = slot ? formatPersonalDate(slot.date) : null;

  return (
    <div
      className={`fixed bottom-24 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-6 md:bottom-28 z-[70] w-[calc(100vw-2rem)] sm:w-[360px] max-w-[360px] transition-all duration-300 pointer-events-auto ${
        closed ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
      }`}
      data-testid="scroll-booking-nudge"
    >
      {/* Speech bubble pointing to the floating button */}
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden animate-bounce-in">
        {/* Close — larger hit area & explicit type/z so it always wins over any leaked overlay */}
        <button
          type="button"
          onClick={dismiss}
          onTouchEnd={dismiss}
          className="absolute top-1.5 right-1.5 w-9 h-9 rounded-full bg-white/95 hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 transition z-20 shadow-sm ring-1 ring-slate-200"
          aria-label="Zapri"
          data-testid="nudge-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header row with avatar */}
        <div className="flex items-start gap-3 p-4 pb-2">
          {/* Team avatar — group photo */}
          <div className="relative flex-shrink-0">
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_0beaa431-9a4b-4a12-bf3a-530e03352013/artifacts/gnaahkd0_cab3911f-ecbb-4e33-bb53-c51430e8a2ca.jpeg"
              alt="Fizio team - Fizioterapija Saša Panič"
              className="w-12 h-12 rounded-full object-cover object-top ring-2 ring-emerald-400 bg-gradient-to-br from-pink-100 to-rose-50"
              loading="lazy"
              width="48"
              height="48"
            />
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full" />
          </div>
          <div className="flex-1 min-w-0 pr-10">
            <div className="text-[11px] uppercase tracking-wider text-emerald-600 font-bold">Online · Pravkar</div>
            <div className="font-bold text-[#1a365d]">Fizio team</div>
          </div>
        </div>

        {/* Message */}
        <div className="px-4 pb-4">
          <p className="text-slate-700 leading-relaxed text-sm mb-3">
            {slot && dateLabel ? (
              <>
                <span className="text-lg">👋</span> Pozdravljeni! Prvi prost termin imamo <strong className="text-[#1a365d]">{dateLabel} ob {slot.time}</strong>. <span className="text-slate-500">Vam ustreza?</span>
              </>
            ) : (
              <>
                <span className="text-lg">👋</span> Pozdravljeni! Bi vam pomagala najti termin? <span className="text-slate-500">Hitro in brez napotnice.</span>
              </>
            )}
          </p>

          {/* Slot summary chip (only when slot exists) */}
          {slot && (
            <div className="flex items-center gap-2 bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100 rounded-xl px-3 py-2 mb-3" data-testid="nudge-slot-chip">
              <Calendar className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span className="text-xs font-semibold text-[#1a365d] truncate">
                {dateLabel} · {slot.time}
              </span>
              <Clock className="w-4 h-4 text-emerald-600 ml-auto flex-shrink-0" />
            </div>
          )}

          {/* CTAs — primary accepts slot, secondary lets user pick own */}
          <div className="space-y-2">
            <button
              onClick={goToBooking}
              className="w-full bg-gradient-to-r from-[#f87171] to-[#ef4444] hover:from-[#ef4444] hover:to-[#dc2626] text-white font-semibold rounded-xl py-2.5 px-3 text-sm inline-flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg transition-all"
              data-testid="nudge-cta"
            >
              {slot ? 'Da, ustreza — rezerviraj' : 'Naroči se online'}
              <ArrowRight className="w-4 h-4" />
            </button>
            {slot && (
              <button
                onClick={goToBookingPickOwn}
                className="w-full bg-white border-2 border-slate-200 hover:border-[#1a365d] hover:bg-slate-50 text-[#1a365d] font-semibold rounded-xl py-2 px-3 text-sm transition-all"
                data-testid="nudge-pick-own"
              >
                Želim drug termin
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
